import { useQuery } from '@apollo/client/react'
import { RoleEnum, TrainerCapability } from '@zeus/index'
import { useTranslation } from 'react-i18next'
import AttendanceButton from '~/components/AttendanceButton'
import Spinner from '~/components/Spinner'
import { GQL_USER_SESSIONS } from '~/gql/sessions/sessions'
import { useAuth } from '~/helpers/store/auth'
import { useAttendanceDownload } from '~/hooks/use-attendance-download'
import { useCan } from '~/hooks/use-can'
import { useCertificateDownload } from '~/hooks/use-certificate-download'
import ProfileActivityGrid from './ProfileActivityGrid'
import ProfileCertificates from './ProfileCertificates'
import ProfileSessions from './ProfileSessions'

/**
 * Sessions et attestations de l'apprenant (#242). Elles viennent de `userSessions`,
 * qui garde sa capacité (`SESSIONS_VIEW`) : sans elle, le formateur ne voit pas ces blocs.
 * Le relevé d'assiduité de chaque session (#322) est réservé à l'admin, comme sa route.
 */
function LearnerActivity({ userId }: { userId: string }) {
  const { t } = useTranslation()
  const isAdmin = useAuth().payload?.role === RoleEnum.Admin
  const canViewSessions = useCan(TrainerCapability.SESSIONS_VIEW)
  const { download, downloadingId } = useCertificateDownload(userId)
  const attendance = useAttendanceDownload(userId)
  const { data } = useQuery(GQL_USER_SESSIONS, {
    variables: { userId },
    skip: !canViewSessions,
    fetchPolicy: 'cache-and-network',
  })

  if (!canViewSessions) return null
  // Rien tant que la réponse n'est pas là : « aucune session » serait un faux zéro.
  if (!data) return <Spinner className="mx-auto size-6 text-muted-foreground" />
  const sessions = data.userSessions

  return (
    <ProfileActivityGrid
      sessions={(
        <ProfileSessions
          sessions={sessions}
          renderAction={isAdmin
            ? session => (
              <AttendanceButton
                variant="ghost"
                size="sm"
                onClick={() => void attendance.download(session.id)}
                loading={attendance.downloadingId === session.id}
                aria-label={t('attendance.downloadFor', { name: session.name })}
              >
                {t('attendance.single')}
              </AttendanceButton>
            )
            : undefined}
        />
      )}
      certificates={<ProfileCertificates sessions={sessions} onDownload={id => void download(id)} downloadingId={downloadingId} />}
    />
  )
}

export default LearnerActivity
